import type { CardTemplate, TopDomain } from "./types";
import { DOMAIN_COLORS } from "./constants";

/** Returns the hex color for a CliftonStrengths domain. */
export function domainColor(domain: string): string {
  return DOMAIN_COLORS[domain] ?? "#888";
}

/** CSS background value for a given template + card variant. */
export function getCardBg(template: CardTemplate, variant: "instagram" | "linkedin"): string {
  if (template === "minimal") return "#101318";
  if (template === "classic") return "linear-gradient(160deg, #0e1628 0%, #1b2745 100%)";
  return variant === "instagram"
    ? "linear-gradient(145deg, #0d1117 0%, #1a1f2e 50%, #0d1117 100%)"
    : "linear-gradient(130deg, #0d1117 0%, #1a1f2e 60%, #111827 100%)";
}

/** Inline styles for a single strength row. */
export function getRowStyle(
  template: CardTemplate,
  color: string,
  linkedin = false
): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    ...(linkedin ? {} : { flex: 1 }),
  };
  if (template === "bold") {
    return {
      ...base,
      gap: 14,
      background: "rgba(255,255,255,0.04)",
      borderRadius: 12,
      padding: linkedin ? "10px 14px" : "0 18px",
      border: `1px solid ${color}30`,
    };
  }
  if (template === "minimal") {
    return {
      ...base,
      gap: 14,
      background: "transparent",
      borderRadius: 0,
      padding: linkedin ? "7px 4px" : "0 4px",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    };
  }
  // classic — thick inline-start accent (auto-adapts to LTR / RTL)
  return {
    ...base,
    gap: 12,
    background: "rgba(255,255,255,0.02)",
    borderRadius: 4,
    padding: linkedin ? "10px 14px 10px 8px" : "0 18px 0 0",
    borderInlineStart: `4px solid ${color}`,
  };
}

/** Inline styles for the rank-number badge inside a strength row. */
export function getRankStyle(
  template: CardTemplate,
  color: string,
  size: number
): React.CSSProperties {
  const base: React.CSSProperties = {
    minWidth: size,
    height: size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: size * 0.42,
    fontWeight: template === "minimal" ? 500 : 800,
  };
  if (template === "bold") {
    return { ...base, borderRadius: size * 0.25, background: `${color}22`, border: `2px solid ${color}`, color };
  }
  if (template === "minimal") {
    return { ...base, borderRadius: "50%", background: "transparent", border: `1px solid ${color}55`, color };
  }
  // classic
  return { ...base, borderRadius: 4, background: color, border: "none", color: "#fff" };
}

/**
 * Derives the top-N dominant domains from the user's strengths.
 * Used as a fallback when the parent does not supply pre-fetched `topDomains`.
 */
export function computeTopDomains(
  strengths: Array<{ domain: string }>,
  n = 2
): TopDomain[] {
  const counts: Record<string, number> = {};
  strengths.slice(0, 10).forEach((s) => {
    counts[s.domain] = (counts[s.domain] ?? 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([domain, count]) => ({ domain, count }));
}
